import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FileSpreadsheet, FileText, Palette, Presentation } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import { ResourceItem } from "./types";
import { getResourcePresentation } from "./resourcePresentation";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

export default function ResourcePreview({ resource }: { resource: ResourceItem }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  const presentation = getResourcePresentation(resource);
  const { extension } = presentation;
  const isPdf = presentation.key === "pdf";

  useEffect(() => {
    if (!isPdf || !resource.destination_url) return;
    let cancelled = false;
    const loadingTask = pdfjsLib.getDocument(resource.destination_url);

    loadingTask.promise
      .then(async (pdf) => {
        const page = await pdf.getPage(1);
        if (cancelled || !canvasRef.current) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(1.2, 140 / baseViewport.width);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext("2d");
        if (!context) return;
        await page.render({ canvas, canvasContext: context, viewport }).promise;
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      void loadingTask.destroy();
    };
  }, [isPdf, resource.destination_url]);

  if (isPdf && !failed) {
    return <Box component="canvas" ref={canvasRef} sx={{ maxWidth: "100%", maxHeight: "96px", bgcolor: "white", boxShadow: "0 2px 8px rgba(0,0,0,.12)" }} />;
  }

  const Icon = presentation.key === "canva"
    ? Palette
    : ["ppt", "pptx"].includes(extension)
    ? Presentation
    : ["xls", "xlsx", "csv"].includes(extension)
      ? FileSpreadsheet
      : FileText;

  return <Box textAlign="center" color="inherit">
    <Icon size={34} strokeWidth={1.45} />
    <Typography
      title={extension || resource.resource_type_display}
      sx={{
        maxWidth: 82,
        mx: "auto",
        fontSize: 8.5,
        fontWeight: 800,
        textTransform: "uppercase",
        mt: 0.5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {presentation.label}
    </Typography>
  </Box>;
}
